import CarouselCardSmall from 'components/OfferList/OfferCards/OfferCardSmall/CarouselCardSmall'
import type { ComponentProps } from 'react'
import { useMemo } from 'react'
import OfferLabels from 'components/Common/Labels/OfferLabels'
import BookmarkButton from 'tripPlanner/components/Bookmark/BookmarkButton'
import LabelGroup from 'LuxKit/Label/LabelGroup'
import OfferBadgeLabel from 'components/Common/OfferBadgeLabel/OfferBadgeLabel'
import VillaCardInclusions from './VillaCardInclusions'
import LEOfferPriceDetails from './LEOfferPriceDetails'
import VillaLocationCaption from './VillaLocation'
import CarouselCardMedium from './OfferCardMedium/CarouselCardMedium'
import { isOfferRatingDisplayable } from 'lib/order/reviewUtils'
import { generateLoyaltyPointsCalculatorVillasOptions } from 'loyalty/lib/pointsCalculation/calculatorOptionsGenerators'
import { useOfferPackagePrice } from 'hooks/Offers/useOfferPriceNew'
import LoyaltyPointsEarn from 'loyalty/components/Earn/LoyaltyPointsEarn'
import { VILLAS_REVIEWS_TOTAL_THRESHOLD } from 'constants/review'

type InheritedCarouselCardMediumProps = Pick<ComponentProps<typeof CarouselCardMedium>, 'variant' | 'eagerLoadImage'>

interface Props extends InheritedCarouselCardMediumProps {
  offer: App.VillaOffer | App.VillaOfferSummary
  onClick: () => void
  offerUrl: string
  className?: string
  size: 'small' | 'medium'
}

function VillaOfferCard(props: Props) {
  const {
    offer,
    onClick,
    className,
    offerUrl,
    size,
    variant,
    eagerLoadImage,
  } = props

  const pkg = offer.lowestPricePackage
  const {
    default: defaultPricing,
    member: memberPricing,
  } = useOfferPackagePrice(pkg as App.Package, offer)

  const pointsEarnCalculationRequests = useMemo<Array<App.LoyaltyPointsEarnCalculationRequest | undefined>>(() => {
    if (offer.bundledWithFlightsOnly) {
      return []
    }
    const propertyFees = defaultPricing.fees?.find((fee) => fee.type === 'property')?.amount ?? 0
    return [
      generateLoyaltyPointsCalculatorVillasOptions(offer, {
        price: defaultPricing.price,
        memberPrice: memberPricing?.price,
        pkg,
        propertyFees,
        nights: pkg?.duration,
      }),
    ]
  }, [pkg, defaultPricing.price, memberPricing?.price, offer, defaultPricing.fees])

  if (offer.isSoldOut) return null

  if (size === 'medium') {
    return <CarouselCardMedium
      variant={variant}
      className={className}
      eagerLoadImage={eagerLoadImage}
      bookmarkButton={<BookmarkButton offer={offer} withLabel="tablet" />}
      image={offer.image}
      images={offer.images}
      location={<VillaLocationCaption offer={offer} />}
      title={offer.name}
      priceDetails={<LEOfferPriceDetails variant="condensed" offer={offer} />}
      to={offerUrl}
      rating={isOfferRatingDisplayable(offer.property.rating, VILLAS_REVIEWS_TOTAL_THRESHOLD) ? offer.property.rating : undefined}
      description={<VillaCardInclusions offer={offer} offerUrl={offerUrl} />}
      urgencyLabels={<LabelGroup>
        <OfferLabels offer={offer} />
        {offer.badge && <OfferBadgeLabel badge={offer.badge} />}
      </LabelGroup>}
      onClick={onClick}
      loyaltyPointsElement={<LoyaltyPointsEarn calculationRequests={pointsEarnCalculationRequests} calculationType="estimate" />}
    />
  } else if (size === 'small') {
    return <CarouselCardSmall
      variant={variant}
      className={className}
      eagerLoadImage={eagerLoadImage}
      bookmarkButton={<BookmarkButton offer={offer} withLabel="tablet" />}
      description={<VillaCardInclusions offer={offer} offerUrl={offerUrl} />}
      image={offer.image}
      images={offer.images}
      location={<VillaLocationCaption offer={offer} />}
      title={offer.property.name}
      priceDetails={<LEOfferPriceDetails offer={offer} />}
      to={offerUrl}
      rating={isOfferRatingDisplayable(offer.property.rating, VILLAS_REVIEWS_TOTAL_THRESHOLD) ? offer.property.rating : undefined}
      urgencyLabels={<LabelGroup>
        <OfferLabels offer={offer} />
        {offer.badge && <OfferBadgeLabel badge={offer.badge} />}
      </LabelGroup>}
      onClick={onClick}
      loyaltyPointsElement={<LoyaltyPointsEarn calculationRequests={pointsEarnCalculationRequests} calculationType="estimate" />}
    />
  }
  return null
}

export default VillaOfferCard
